import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Zap, Crown } from "lucide-react";
import { api } from "../lib/api";

const Courses = () => {
    const [mini, setMini] = useState(null);
    const [big, setBig] = useState(null);

    useEffect(() => {
        api.get("/mini-course").then((r) => setMini(r.data));
        api.get("/course").then((r) => setBig(r.data)).catch(() => {});
    }, []);

    return (
        <div className="bg-black" data-testid="courses-page">
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-red-700/15 blur-3xl rounded-full pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
                    <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.5em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" /> Train For The Battle <span className="h-px w-12 bg-red-600" />
                    </div>
                    <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl lg:text-8xl">
                        Pick your <br /> <span className="text-red-600">battlefront.</span>
                    </h1>
                    <p className="mt-8 max-w-2xl mx-auto text-white/75 text-lg leading-relaxed">
                        A 20-minute crash course to start fighting today — or the full BattleFront training when you're ready for the long game.
                    </p>
                </div>
            </section>

            <section className="py-20 md:py-24 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-8">
                    {/* MINI COURSE — TOP / FIRST */}
                    {mini && (
                        <Link to="/daily-battle-drill" className="group border-2 border-red-700/60 bg-gradient-to-br from-red-950/30 to-black p-8 md:p-10 shadow-[0_0_80px_-20px_rgba(185,28,28,0.5)] hover:border-red-600 transition-colors" data-testid="card-mini">
                            <div className="flex items-center justify-between mb-6">
                                <div className="bg-red-700/20 border border-red-700/40 p-3"><Zap size={26} className="text-red-500" /></div>
                                <span className="font-stencil text-[10px] tracking-[0.3em] bg-red-700 text-white px-3 py-1">START HERE</span>
                            </div>
                            <div className="font-stencil text-[10px] tracking-widest text-red-500">MINI COURSE · INSTANT ACCESS</div>
                            <h2 className="mt-3 font-display text-3xl md:text-4xl uppercase tracking-tighter leading-tight">{mini.title}</h2>
                            <p className="mt-3 text-white/70 text-sm font-display tracking-tight uppercase">{mini.tagline}</p>
                            <p className="mt-5 text-white/70 leading-relaxed">{mini.description}</p>
                            <ul className="mt-6 space-y-2 text-sm">
                                {mini.what_you_get.slice(0, 4).map((w, i) => (
                                    <li key={i} className="flex items-start gap-2 text-white/80"><Check size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> {w}</li>
                                ))}
                            </ul>
                            <div className="mt-8 flex items-end justify-between pt-6 border-t border-white/10">
                                <div className="font-display text-5xl text-white">${mini.price_usd}</div>
                                <div className="flex items-center gap-2 font-display text-sm uppercase tracking-tight text-red-500 group-hover:text-red-400">
                                    Get Instant Access <ArrowRight size={16} />
                                </div>
                            </div>
                        </Link>
                    )}

                    {/* FULL BATTLEFRONT */}
                    {big && (
                        <Link to="/course" className="group border border-white/15 bg-[#0a0a0a] p-8 md:p-10 hover:border-red-600/50 transition-colors" data-testid="card-big">
                            <div className="flex items-center justify-between mb-6">
                                <div className="bg-white/5 border border-white/15 p-3"><Crown size={26} className="text-red-600" /></div>
                                <span className="font-stencil text-[10px] tracking-[0.3em] border border-white/20 text-white/70 px-3 py-1">FULL TRAINING</span>
                            </div>
                            <div className="font-stencil text-[10px] tracking-widest text-red-500">FLAGSHIP COURSE</div>
                            <h2 className="mt-3 font-display text-3xl md:text-4xl uppercase tracking-tighter leading-tight">{big.title}</h2>
                            <p className="mt-5 text-white/70 leading-relaxed">{big.subtitle || "The complete training to win the war in your mind — 5 modules, 21 lessons, the long game mapped out step-by-step."}</p>
                            <ul className="mt-6 space-y-2 text-sm">
                                <li className="flex items-start gap-2 text-white/80"><Check size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> {big.modules?.length || 5} battlefronts · {big.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 21} lessons</li>
                                <li className="flex items-start gap-2 text-white/80"><Check size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> Workbooks, infographics, daily prompts</li>
                                <li className="flex items-start gap-2 text-white/80"><Check size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> Spiritual · Emotional · Chemical fronts</li>
                                <li className="flex items-start gap-2 text-white/80"><Check size={16} className="text-red-600 mt-0.5 flex-shrink-0" /> Lifetime access · Watch at your own pace</li>
                            </ul>
                            <div className="mt-8 flex items-end justify-between pt-6 border-t border-white/10">
                                <div className="font-display text-5xl text-white">${big.price_usd || 97}</div>
                                <div className="flex items-center gap-2 font-display text-sm uppercase tracking-tight text-red-500 group-hover:text-red-400">
                                    View Course <ArrowRight size={16} />
                                </div>
                            </div>
                        </Link>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Courses;
